/**
 * 쿼리 프로파일러 백엔드 호출 (P6spyLogController)
 *
 *  POST /snotes/api/querySwitch            { status: on|off, key }
 *  POST /snotes/api/querySwitch/getLogs    { key }   → string[]
 *  POST /snotes/api/querySwitch/getIpLogs  { key }   → string[] (요청 IP 것만)
 *  POST /snotes/api/querySwitch/clearLog   { key }
 *
 * 테넌트는 X-TENANT-ID 헤더로 지정한다. 세션 쿠키가 필요하므로
 * 다른 서버를 지정할 때는 그 서버에 로그인된 상태이고 CORS 허용이 되어 있어야 한다.
 */

const PROFILER_KEY = "uK7mX9qP2vN5wZ1sB8tR4eY6xC3jA1";
const CONTEXT_PATH = "/snotes";

export class ProfilerApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/** 서버주소 입력값 → fetch 접두어. 비어 있거나 현재 origin 이면 상대경로 사용 */
export function resolveBaseUrl(serverUrl: string): string {
  const url = (serverUrl || "").trim().replace(/\/+$/, "");
  if (url === "" || url === window.location.origin) return "";
  return url;
}

async function post(
  baseUrl: string,
  path: string,
  tenantId: string,
  body: Record<string, string>
): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(`${baseUrl}${CONTEXT_PATH}/api/querySwitch${path}`, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json, text/plain",
        "Content-Type": "application/json",
        "X-TENANT-ID": tenantId || "DEFAULT",
      },
      body: JSON.stringify({ key: PROFILER_KEY, ...body }),
    });
  } catch (e) {
    throw new ProfilerApiError(
      0,
      "서버에 연결할 수 없습니다. 백엔드가 실행 중인지 확인하세요."
    );
  }

  if (!response.ok) {
    if (response.status === 401 || response.status === 498) {
      throw new ProfilerApiError(
        response.status,
        "세션이 없거나 만료되었습니다. 대상 서버에 로그인되어 있어야 합니다."
      );
    }
    throw new ProfilerApiError(
      response.status,
      `요청 실패 (HTTP ${response.status})`
    );
  }
  return response;
}

/** 추적 ON/OFF. 서버가 돌려주는 안내 문구를 반환 */
export async function switchProfiler(
  baseUrl: string,
  tenantId: string,
  on: boolean
): Promise<string> {
  const res = await post(baseUrl, "", tenantId, { status: on ? "on" : "off" });
  return res.text();
}

/** 누적 로그 조회. ipOnly 면 내 IP 로그만 */
export interface ProfilerLogs {
  logs: string[];
  /** 응답 헤더 X-Profiler-Enabled 값. 옛 백엔드는 헤더가 없어 null */
  enabled: boolean | null;
}

// 서버 스위치 상태도 함께 돌려준다 (다른 곳에서 꺼진 것을 폴링 중에 알기 위해)
export async function fetchProfilerLogs(
  baseUrl: string,
  tenantId: string,
  ipOnly: boolean
): Promise<ProfilerLogs> {
  const res = await post(
    baseUrl,
    ipOnly ? "/getIpLogs" : "/getLogs",
    tenantId,
    {}
  );
  const data = await res.json();
  const header = res.headers.get("X-Profiler-Enabled");
  return {
    logs: Array.isArray(data) ? data : [],
    enabled: header === null ? null : header === "true",
  };
}

/** 서버측 버퍼 비우기 */
export async function clearProfilerLogs(
  baseUrl: string,
  tenantId: string
): Promise<void> {
  await post(baseUrl, "/clearLog", tenantId, {});
}

export interface ProfilerStatus {
  enabled: boolean;
  logCount: number;
}

/** 서버 추적 스위치 상태와 버퍼 건수 (백엔드에 /status 가 있어야 함) */
export async function fetchProfilerStatus(
  baseUrl: string,
  tenantId: string
): Promise<ProfilerStatus> {
  let res: Response;
  try {
    res = await post(baseUrl, "/status", tenantId, {});
  } catch (e) {
    if (e instanceof ProfilerApiError && e.status === 404) {
      throw new ProfilerApiError(
        404,
        "이 서버 백엔드에는 상태확인 API 가 없습니다. 백엔드 배포가 필요합니다."
      );
    }
    throw e;
  }
  const data = await res.json();
  if (!data || data.ok !== true) {
    throw new ProfilerApiError(400, "상태 조회가 거부되었습니다.");
  }
  return {
    enabled: data.enabled === true,
    logCount: Number(data.logCount) || 0,
  };
}
